import React from 'react';
import { Margin } from '@nib/layout';
import { FieldsetFormControl } from '@nib-components/form-control';
import _cloneDeep from 'lodash/cloneDeep';
import { isTouched, isIdFromObject, isDateValid, dateErrorMessage } from '../../util/validationHelpers';
import { AddFunnelPropFromContext } from '../funnel-context';
import { FUNNELS, getFormControlType } from '../../util/constants';
import { createDateFieldString, addCurrentTargetValueToFormik, addValidatedDOBFieldToFormik, isValidDateFormat, isFormikFieldValid, isFormikFieldTouched, getFormikDateFieldValue, setFocusToNextField, setFocusToPreviousField, setHiddenFieldTouched, handleFieldAlreadyFilled, addPrecedingZeroIfSubTenValue, getPreviousDateFieldValues, isAllDateFieldsEmpty, generateNewValuesState, generateNewTouchedState, extractDateDetails } from './commonDateHelpers';
import NumberTextbox from './numberTextbox';
import { validDate } from '@nib/validation';
const HELP_TEXTS = {
  ARHI: 'DD-MM-YYYY'
};
const CommonDate = props => {
  const {
    id,
    label,
    help,
    field,
    form,
    valid,
    validated,
    error,
    onDateBlur,
    formMode = 'light',
    funnel = FUNNELS.ARHI,
    validationFunction = validDate,
    optional
  } = props;
  // this variable helps dynamic generation of fields as well as provides the name for the field submitted to the server, eg Commondate
  const fieldName = (field === null || field === void 0 ? void 0 : field.name) || '';
  const dateFieldNames = {
    day: `${fieldName}Day`,
    month: `${fieldName}Month`,
    year: `${fieldName}Year`
  };
  /*
    Formik only passes through one form field, if we pass this field down to each input eg <NumberTextbox {...field}/>, each input will all inherit the same event functions.
    This would result in them all sharing the same onChange/onBlur event, instead we pass our custom onChange={handleChange} onBlur for formik only
  */
  const handleChange = e => {
    var _a;
    const fieldType = e.target.getAttribute('data-fieldtype');
    // is a child field eg child.0.Commondate
    const isFieldArray = isIdFromObject(e.target.id);
    const targetFieldValue = (_a = e.target.value) === null || _a === void 0 ? void 0 : _a.replace(/\D/g, '');
    const cursorPosition = e.target.selectionStart || 0;
    // maxlength doesn't exist even when on field
    const maxLength = e.target.getAttribute('data-maxlength');
    if ((targetFieldValue === null || targetFieldValue === void 0 ? void 0 : targetFieldValue.length) > maxLength && cursorPosition > 2 && fieldType !== 'year') {
      // adds value to next field.
      handleFieldAlreadyFilled(form === null || form === void 0 ? void 0 : form.setFieldValue, e, dateFieldNames, maxLength, fieldType);
      return false;
    }
    // We should only need to deep copy for when dependants exist in form.values as they contain child objects.
    let formikValues = isFieldArray ? _cloneDeep(form === null || form === void 0 ? void 0 : form.values) : {
      ...(form === null || form === void 0 ? void 0 : form.values)
    };
    const {
      day,
      month,
      year,
      hiddenField
    } = getPreviousDateFieldValues(isFieldArray, formikValues, dateFieldNames, fieldName);
    /*
      Let's strip non numbers and trim down the value before it goes to formikValues
      When isValidDateFormat further down was true the line form.setFieldValue(e.target.id, trimmedFieldValue);
      was never being hit, but the line form.setValues(formikValues);
      Was updating formik with the non trimmed result.
      This resulted in this bug PHISL-3442
    */
    e.target.value = targetFieldValue === null || targetFieldValue === void 0 ? void 0 : targetFieldValue.substring(0, maxLength);
    formikValues = addCurrentTargetValueToFormik(isFieldArray, formikValues, e);
    const dateFieldValue = createDateFieldString(isFieldArray, formikValues, dateFieldNames);
    if ((targetFieldValue === null || targetFieldValue === void 0 ? void 0 : targetFieldValue.length) === 0 && (form === null || form === void 0 ? void 0 : form.setFieldTouched)) {
      form === null || form === void 0 ? void 0 : form.setFieldTouched(e.target.id, false);
    }
    if (((day === null || day === void 0 ? void 0 : day.length) > 0 || (month === null || month === void 0 ? void 0 : month.length) > 0 || (year === null || year === void 0 ? void 0 : year.length) === 4) && (form === null || form === void 0 ? void 0 : form.setFieldTouched)) {
      form.setFieldTouched(fieldName, true);
    } else if (!(form === null || form === void 0 ? void 0 : form.touched[fieldName])) {
      /*
        Only sets it to touched if certain conditions met.
        Eg, date fields are empty but form has had a submission attempt which sets them all to touched.
      */
      if (form) setHiddenFieldTouched(form.touched, form === null || form === void 0 ? void 0 : form.submitCount, form === null || form === void 0 ? void 0 : form.setFieldTouched, fieldName);
    }
    /*
      All fields filled out, isValidDateFormat only checks for yyyy-mm-dd it does not check if valid months etc
      Eg 2000-15-44 would still be in the valid format... but an invalid date.
    */
    if (isValidDateFormat(dateFieldValue)) {
      formikValues = addValidatedDOBFieldToFormik(isFieldArray, formikValues, fieldName, dateFieldValue);
      form === null || form === void 0 ? void 0 : form.setValues(formikValues);
      // validCommonDate actually returns a validation error message when invalid and undefined when valid.
      if (typeof onDateBlur === 'function' && validationFunction(dateFieldValue) === undefined) {
        onDateBlur(e, dateFieldValue);
      }
    } else {
      // Update if it's a number or an empty value, eg someone has cleared the field.
      if ((targetFieldValue === null || targetFieldValue === void 0 ? void 0 : targetFieldValue.length) === 0 || targetFieldValue.match(/^\d/)) {
        // even though Formik obeys the maxlength, I could still see value longer in the dom under rare circumstances so trimming.
        const trimmedFieldValue = targetFieldValue.substring(0, maxLength);
        form === null || form === void 0 ? void 0 : form.setFieldValue(e.target.id, trimmedFieldValue);
        if (fieldType !== 'year') {
          // eg for month of 9 entered, add a zero, but not for 1 as it could be the user wants 01,10,11,12 etc
          addPrecedingZeroIfSubTenValue(e, handleChange, fieldType, isFieldArray, formikValues, dateFieldNames);
        }
        // If a user clears out the DD MM YYYY fields, we don't want the hidden field to keep a value.
        if ((hiddenField === null || hiddenField === void 0 ? void 0 : hiddenField.length) && isAllDateFieldsEmpty(fieldType, trimmedFieldValue, {
          day,
          month,
          year
        })) {
          form === null || form === void 0 ? void 0 : form.setFieldValue(fieldName, '');
        }
      }
    }
  };
  // allow user to move back and forth between fields with left right arrow key as well as via backspacing.
  const handleKeyDown = (e, value) => {
    const fieldType = e.target.getAttribute('data-fieldtype');
    const fieldValue = e.target.value;
    const isLeftArrow = e.keyCode === 37;
    const isRightArrow = e.keyCode === 39;
    const isBackSpace = e.keyCode === 8;
    const cursorPosition = e.target.selectionStart;
    if (isLeftArrow && cursorPosition === 0) {
      setFocusToPreviousField(e, dateFieldNames);
      return false;
    }
    if (isRightArrow && cursorPosition === 2 && fieldType !== 'year') {
      setFocusToNextField(e, dateFieldNames);
      return false;
    }
    if (isBackSpace && cursorPosition === 0) {
      // can't go anywhere back from 'day'
      if (!fieldValue && fieldType !== 'day') {
        form === null || form === void 0 ? void 0 : form.setFieldValue(e.target.id, '');
        setFocusToPreviousField(e, dateFieldNames);
      }
      return false;
    }
  };
  const handleBlur = (e, value) => {
    const fieldType = e.target.getAttribute('data-fieldtype');
    const fieldValue = e.target.value;
    // if user clicks out of the field with a day/month of 4 change it to 04.
    // if they clicks out with 0 in the field just leave it for validation.
    if ((fieldType === 'day' || fieldType === 'month') && (fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.length) === 1 && fieldValue != '0') {
      e.target.value = String(fieldValue).padStart(2, '0');
      handleChange(e);
    }
    form === null || form === void 0 ? void 0 : form.setFieldTouched(e.target.id, true);
  };
  const handlePaste = e => {
    e.preventDefault();
    const fieldType = e.target.getAttribute('data-fieldtype');
    const pastedText = e.clipboardData.getData('text');
    const extractedDateDetails = extractDateDetails(pastedText, fieldType);
    if (extractedDateDetails) {
      form === null || form === void 0 ? void 0 : form.setValues(generateNewValuesState(fieldName, dateFieldNames, form.values, extractedDateDetails));
      form === null || form === void 0 ? void 0 : form.setTouched(generateNewTouchedState(fieldName, dateFieldNames, form.touched, Object.keys(extractedDateDetails)), false);
    }
  };
  // the below 3 lines are for storybook knobs
  const validState = typeof valid !== 'undefined' ? valid : isDateValid(props, fieldName, dateFieldNames);
  const validatedState = typeof validated !== 'undefined' ? validated : isTouched(props);
  const errorState = typeof error !== 'undefined' ? error : dateErrorMessage(props, fieldName, dateFieldNames);
  return React.createElement(FieldsetFormControl, {
    id: id,
    valid: validState,
    validated: validatedState,
    error: errorState,
    label: label || '',
    help: help || HELP_TEXTS[funnel] || HELP_TEXTS.ARHI,
    hasNegativeFieldsetMargin: false,
    formControlType: getFormControlType(props.required, optional)
  }, React.createElement(React.Fragment, null, React.createElement("input", {
    type: "hidden",
    name: fieldName,
    id: fieldName
  }), React.createElement(Margin, {
    inline: true,
    right: 4
  }, React.createElement(NumberTextbox, {
    id: dateFieldNames.day,
    name: dateFieldNames.day,
    label: "Day",
    valid: isFormikFieldValid(form, dateFieldNames.day),
    validated: isFormikFieldTouched(form, dateFieldNames.day),
    width: 2,
    mask: [/\d/, /\d/],
    handleChange: handleChange,
    handleBlur: handleBlur,
    handleKeyDown: handleKeyDown,
    onPaste: handlePaste,
    min: 1,
    max: 31,
    value: getFormikDateFieldValue(form, dateFieldNames.day),
    "data-maxlength": 2,
    "data-fieldtype": "day",
    formMode: formMode,
    funnel: funnel
  })), React.createElement(Margin, {
    inline: true,
    right: 4
  }, React.createElement(NumberTextbox, {
    id: dateFieldNames.month,
    name: dateFieldNames.month,
    label: "Month",
    valid: isFormikFieldValid(form, dateFieldNames.month),
    validated: isFormikFieldTouched(form, dateFieldNames.month),
    width: 2,
    mask: [/\d/, /\d/],
    handleChange: handleChange,
    handleBlur: handleBlur,
    handleKeyDown: handleKeyDown,
    onPaste: handlePaste,
    value: getFormikDateFieldValue(form, dateFieldNames.month),
    "data-maxlength": 2,
    "data-fieldtype": "month",
    formMode: formMode,
    funnel: funnel
  })), React.createElement(Margin, {
    inline: true
  }, React.createElement(NumberTextbox, {
    id: dateFieldNames.year,
    name: dateFieldNames.year,
    label: "Year",
    valid: isFormikFieldValid(form, dateFieldNames.year),
    validated: isFormikFieldTouched(form, dateFieldNames.year),
    width: 4,
    mask: [/\d/, /\d/, /\d/, /\d/],
    handleChange: handleChange,
    handleBlur: handleBlur,
    handleKeyDown: handleKeyDown,
    onPaste: handlePaste,
    value: getFormikDateFieldValue(form, dateFieldNames.year),
    "data-maxlength": 4,
    "data-fieldtype": "year",
    formMode: formMode,
    funnel: funnel
  }))));
};
CommonDate.defaultProps = {
  formMode: 'light'
};
const CommonDateWithFunnel = props => React.createElement(AddFunnelPropFromContext, null, React.createElement(CommonDate, {
  ...props
}));
export default CommonDateWithFunnel;